import React from "react";
import { motion } from "framer-motion";
import { VideoIcon, Share2Icon, ImageIcon } from "@radix-ui/react-icons";
import { ShoppingBag, Layers, Users, MessageCircle, Phone, Icon } from "lucide-react";

export default function Features() {
  const features = [
    {
      Icon: "/images/reels_icon.png",
      name: "Create Short Reels",
      description:
        "Create engaging short-form video content with powerful editing tools and trending effects.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl" />
          {/* Video camera pattern */}
          <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-lg rotate-12 flex items-center justify-center">
            <div className="w-6 h-6 bg-white/40 rounded-full"></div>
          </div>
          <div className="absolute bottom-8 right-12 w-8 h-8 bg-gradient-to-br from-blue-400/40 to-cyan-400/40 rounded-full"></div>
          <div className="absolute top-16 right-16 w-4 h-4 bg-gradient-to-br from-blue-300/50 to-cyan-300/50 rounded-full"></div>
          {/* Film strip pattern */}
          <div className="absolute bottom-0 right-0 w-full h-8 bg-gradient-to-r from-transparent via-blue-500/10 to-cyan-500/10">
            <div className="flex h-full items-center justify-end space-x-1 pr-4">
              <div className="w-2 h-2 bg-blue-400/30 rounded-full"></div>
              <div className="w-2 h-2 bg-cyan-400/30 rounded-full"></div>
              <div className="w-2 h-2 bg-blue-400/30 rounded-full"></div>
            </div>
          </div>
        </div>
      ),
      color: "bg-grad-orange-1"
    },
    {
      Icon: "/images/pix_icon.png",
      name: "Share Pixs",
      description:
        "Share your favorite moments and discoveries with the global community instantly.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full blur-3xl" />
          {/* Photo frames */}
          <div className="absolute top-4 right-4 w-12 h-9 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-md rotate-12 border border-white/20"></div>
          <div className="absolute top-8 right-12 w-10 h-8 bg-gradient-to-br from-green-400/25 to-emerald-400/25 rounded-md -rotate-6 border border-white/15"></div>
          <div className="absolute bottom-6 right-6 w-8 h-6 bg-gradient-to-br from-green-300/30 to-emerald-300/30 rounded-md rotate-45 border border-white/20"></div>
          {/* Share arrows */}
          <div className="absolute bottom-4 right-16 w-6 h-1 bg-gradient-to-r from-green-400/40 to-emerald-400/40 rounded-full"></div>
          <div className="absolute bottom-3 right-14 w-2 h-2 bg-green-400/40 rounded-full"></div>
        </div>
      ),
      color: "bg-brand-orange"
    },
    {
      Icon: "/images/pouch_icon.png",
      name: "Build Pix Pouches",
      description:
        "Organize and curate collections of public pixs that inspire you and others.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-10 -top-10 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-2xl" />
          {/* Stacked cards */}
          <div className="absolute top-4 right-4 w-10 h-12 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-lg border border-white/20"></div>
          <div className="absolute top-5 right-5 w-10 h-12 bg-gradient-to-br from-purple-400/25 to-pink-400/25 rounded-lg border border-white/15"></div>
          <div className="absolute top-6 right-6 w-10 h-12 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-lg border border-white/20"></div>
          {/* Grid pattern */}
          <div className="absolute bottom-4 right-4 grid grid-cols-2 gap-1">
            <div className="w-2 h-2 bg-purple-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-pink-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-pink-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-purple-400/30 rounded-sm"></div>
          </div>
        </div>
      ),
      color: "bg-grad-orange-2"
    },
    {
      Icon: "/images/memorycapsule.jpeg",
      name: "Memory Capsule",
      description:
        "Save meaningful moments, milestones, or stories in private time-locked capsules.Unlock and relive them later—just like opening a memory you preserved for your future self.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          {/* Glow Circles */}
          <div className="absolute -left-10 -top-10 w-64 h-64 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-full blur-2xl" />
    
          {/* Gift Box Patterns */}
          
    
          {/* Dots Pattern */}
          <div className="absolute bottom-4 right-4 grid grid-cols-2 gap-1">
            <div className="w-2 h-2 bg-yellow-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-orange-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-orange-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-yellow-400/30 rounded-sm"></div>
          </div>
        </div>
      ),
      color: "bg-orange-old" 
    },
    {
      Icon: "/images/shop_icon.png",
      name: "Shop Products",
      description:
        "Discover and purchase amazing products directly from creators and brands worldwide.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-15 -top-15 w-72 h-72 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full blur-2xl" />
          {/* Shopping bag */}
          <div className="absolute top-4 right-4 w-12 h-10 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-lg border border-white/20">
            <div className="w-6 h-2 bg-white/20 rounded-full mx-auto mt-2"></div>
          </div>
          {/* Price tags */}
          <div className="absolute top-8 right-12 w-6 h-4 bg-gradient-to-br from-orange-400/40 to-red-400/40 rounded-md rotate-12 flex items-center justify-center">
            <div className="w-1 h-1 bg-white/60 rounded-full"></div>
          </div>
          {/* Shopping cart items */}
          <div className="absolute bottom-4 right-4 space-y-1">
            <div className="w-8 h-1 bg-orange-400/30 rounded-full"></div>
            <div className="w-6 h-1 bg-red-400/30 rounded-full"></div>
            <div className="w-7 h-1 bg-orange-300/30 rounded-full"></div>
          </div>
        </div>
      ),
      color: "bg-orange-old"
    },
    {
      Icon: "/images/location1.jpeg",   // <-- your new icon file
      name: "V Maps",
      description:
        "Visualize your activity, interactions, and engagement patterns across Vreels.Discover when and where your presence is strongest with dynamic, privacy-first heat maps.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          {/* Soft Glow */}
          <div className="absolute -right-10 -top-10 w-64 h-64 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 rounded-full blur-2xl" />
    
          {/* Pin Pattern */}
          <div className="absolute top-4 right-4 w-10 h-10 bg-gradient-to-br from-indigo-500/30 to-blue-500/30 rounded-full flex items-center justify-center">
            <div className="w-3 h-3 bg-white/40 rounded-full" />
          </div>
    
          <div className="absolute bottom-6 right-8 w-6 h-6 bg-gradient-to-br from-indigo-300/30 to-blue-300/30 rounded-full" />
    
          {/* Small dots */}
          <div className="absolute bottom-4 right-4 grid grid-cols-2 gap-1">
            <div className="w-2 h-2 bg-indigo-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-blue-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-blue-400/30 rounded-sm"></div>
            <div className="w-2 h-2 bg-indigo-400/30 rounded-sm"></div>
          </div>
        </div>
      ),
      color: "bg-grad-orange-1"
    },    
    {
      Icon: Users,
      name: "Global Community",
      description:
        "Connect with creators, shoppers, and collectors from around the world. Build meaningful connections.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-15 -top-15 w-72 h-72 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-full blur-2xl" />
          {/* User avatars */}
          <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-teal-500/40 to-cyan-500/40 rounded-full border border-white/30"></div>
          <div className="absolute top-6 right-10 w-6 h-6 bg-gradient-to-br from-teal-400/35 to-cyan-400/35 rounded-full border border-white/25"></div>
          <div className="absolute top-10 right-6 w-7 h-7 bg-gradient-to-br from-teal-300/40 to-cyan-300/40 rounded-full border border-white/30"></div>
          {/* Connection lines */}
          <div className="absolute top-8 right-8 w-4 h-0.5 bg-gradient-to-r from-teal-400/30 to-cyan-400/30 rounded-full rotate-45"></div>
          <div className="absolute top-9 right-7 w-3 h-0.5 bg-gradient-to-r from-teal-300/30 to-cyan-300/30 rounded-full -rotate-45"></div>
          {/* Community icons */}
          <div className="absolute bottom-6 right-6 flex space-x-1">
            <div className="w-2 h-2 bg-teal-400/40 rounded-full"></div>
            <div className="w-2 h-2 bg-cyan-400/40 rounded-full"></div>
            <div className="w-2 h-2 bg-teal-300/40 rounded-full"></div>
          </div>
        </div>
      ),
      color: "bg-grad-orange-1"
    },
    {
      Icon: "/images/chat_icon.png",
      name: "Chat & Calls",
      description: "Connect instantly with other users through real-time chat, messaging, and voice/video calls—all in one place.",
      background: (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-10 -top-10 w-64 h-64 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-2xl" />
          {/* Chat bubble */}
          <div className="absolute top-6 right-8 w-8 h-8 bg-gradient-to-br from-blue-400/40 to-green-400/40 rounded-full"></div>
          {/* Call wave */}
          <div className="absolute bottom-8 right-12 w-5 h-5 bg-gradient-to-br from-pink-400/40 to-purple-400/40 rounded-full"></div>
        </div>
      ),
      color: "bg-brand-orange"
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Powerful{" "}
            <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
              Features
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Everything you need to create reels, share pixs, build collections,
            and shop products. Built for creators, collectors, and shoppers.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-6 xl:gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.Icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="h-full p-4 sm:p-5 lg:p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:scale-105 relative overflow-hidden">
                  {/* Background Pattern */}
                  {feature.background}
                  {/* Icon */}
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl shadow-lg mb-4 ${feature.color || 'bg-brand-orange'}`}>
                    {typeof Icon === "string" ? (
                      <img src={Icon} alt={feature.name} className="h-6 w-6" />
                    ) : (
                      <Icon className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    {feature.name}
                  </h3>
                  <p className="text-base text-muted-foreground mb-0">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
